import { CountryCode } from '@dayetopia/library';
import { Environment } from '@contracts';

/**
 * Gets the active environment, defaulting to 'development' if not set
 */
export const getActiveEnvironment = (): Environment => {
  return (process.env.GATSBY_ACTIVE_ENV as Environment) || 'development';
};

/**
 * Checks if the current environment is development.
 *
 * @returns {boolean} True if the environment is development, otherwise false.
 */
export const isDevelopmentEnvironment = (): boolean => {
  return getActiveEnvironment() === 'development';
};

/**
 * Checks if the current environment is test.
 *
 * @returns {boolean} True if the environment is test, otherwise false.
 */
export const isTestEnvironment = (): boolean => {
  return getActiveEnvironment() === 'test';
};

/**
 * Checks if the current environment is production.
 *
 * @returns {boolean} True if the environment is production, otherwise false.
 */
export const isProductionEnvironment = (): boolean => {
  return getActiveEnvironment() === 'prod';
};

/**
 * Checks if the FLEX_PAY feature should be shown.
 *
 * @returns {boolean} True if FLEX_PAY should be shown, otherwise false.
 */
export const isFlexPayEnabled = (): boolean => {
  return process.env.SHOW_FLEX_PAY === 'true';
};

/**
 * Checks if PayPal should be shown for the given country.
 *
 * @param {CountryCode} country - The country code to check
 * @returns {boolean} True if PayPal should be shown, otherwise false.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isPaypalEnabled = (_country?: CountryCode): boolean => {
  return process.env.SHOW_PAYPAL === 'true';
};

/**
 * Gets the PPC questionnaire ID from the environment variables.
 *
 * @returns {string} The PPC questionnaire ID.
 */
export const getPPCQuestionnaireId = (): string => {
  return process.env.PPC_QUESTIONNAIRE_ID || '';
};

/**
 * Gets the Ureaplasma questionnaire ID from the environment variables.
 *
 * @returns {string} The Ureaplasma questionnaire ID.
 */
export const getUreaplasmaQuestionnaireId = (): string => {
  return process.env.UREAPLASMA_QUESTIONNAIRE_ID || '';
};

/**
 * Gets the default treatment questionnaire ID from the environment variables.
 *
 * @returns {string} The default treatment questionnaire ID.
 */
export const getDefaultTreatmentQuestionnaireId = (): string => {
  return process.env.DEFAULT_TREATMENT_QUESTIONNAIRE_ID || '';
};

/**
 * Checks if the donation component should be shown in checkout.
 *
 * @returns {boolean} True if donation should be shown (default), false if disabled.
 */
export const isDonationCheckoutEnabled = (): boolean => {
  return process.env.SHOW_DONATION_CHECKOUT !== 'false';
};

/**
 * Checks if the email MFA feature should be available.
 *
 * @returns {boolean} True if email MFA should be shown, otherwise false.
 */
export const isEmailMfaEnabled = (): boolean => {
  return process.env.SHOW_EMAIL_MFA === 'true';
};
